import { useState, useEffect, useRef, useCallback } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────




// ─── Icon Paths ───────────────────────────────────────────────────────────────

const ICONS = {
  warning: 'M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-10H9v5h1.5zm0 6H9v1.5h1.5z',
  check: 'M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z',
  info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
  approval: 'M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm-2 16l-5-5 1.4-1.4L10 14.2l7.6-7.6L19 8l-9 9z',
  bell: 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z',
  x: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z',
};

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d={d} />
  </svg>
);

const NOTIF_CATEGORY_STYLE = {
  Alert:    { icon: ICONS.warning,     bg: '#fde7e9', color: '#a4262c', border: '#f5b0b3' },
  Action:   { icon: ICONS.check,       bg: '#fce8d3', color: '#ca5010', border: '#f5c6a0' },
  Update:   { icon: ICONS.info,        bg: '#e8f4fd', color: '#0078d4', border: '#b3d4f5' },
  Approval: { icon: ICONS.approval,    bg: '#f3f0fd', color: '#5c2d91', border: '#c8b8f0' },
  System:   { icon: ICONS.bell,        bg: '#f0f2f5', color: '#5c6070', border: '#d6dae3' },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── NotificationPanel Component ──────────────────────────────────────────────



export function NotificationPanel({
  notifications,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onClose,
  onViewAll,
}) {
  const [filter, setFilter] = useState('All');
  const panelRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const unreadCount = notifications?.filter(n => !n.read)?.length;
  const categories = ['All', 'Alert', 'Action', 'Approval', 'Update', 'System'];
  const filtered = filter === 'All' ? notifications : notifications?.filter(n => n.category === filter);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute',
        top: 50,
        right: 0,
        width: 380,
        maxHeight: 480,
        background: '#fff',
        border: '1px solid #e5e8ee',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {/* Inline style to hide the category-pill scrollbar (no external CSS file needed) */}
      <style>{`
        .notif-pill-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f2f5' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#161b2e' }}>Notifications</span>
            {unreadCount > 0 && (
              <span
                style={{
                  background: '#cf1322',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: 1,
                  borderRadius: 10,
                  minWidth: 32,
                  padding: '4px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {unreadCount} new
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                style={{
                  fontSize: 11,
                  color: '#0078d4',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#8b92a5',
                display: 'flex',
                padding: 2,
                borderRadius: 4,
              }}
            >
              <Icon d={ICONS.x} size={14} />
            </button>
          </div>
        </div>

        {/* Category filter pills */}
        <div
          className="notif-pill-scroll"
          style={{
            display: 'flex',
            gap: 4,
            overflowX: 'auto',
            paddingBottom: 0,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {categories.map(cat => {
            const isActive = filter === cat;
            const style_ = cat !== 'All' ? NOTIF_CATEGORY_STYLE[cat] : null;
            const count = cat === 'All' ? notifications?.length : notifications?.filter(n => n.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                style={{
                  padding: '3px 10px',
                  borderRadius: 14,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: '1px solid',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  background: isActive ? (style_?.bg ?? '#161b2e') : '#fff',
                  color: isActive ? (style_?.color ?? '#fff') : '#8b92a5',
                  borderColor: isActive ? (style_?.border ?? '#161b2e') : '#e5e8ee',
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {cat} {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered?.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#8b92a5', fontSize: 13 }}>
            No notifications in this category.
          </div>
        )}
        {filtered?.map((notif, i) => {
          const cs = NOTIF_CATEGORY_STYLE[notif.category];
          return (
            <div
              key={notif.id}
              onClick={() => onMarkRead(notif.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onMarkRead(notif.id);
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                
                padding: '2px 12px 0px 12px',
                borderBottom: i < filtered?.length - 1 ? '1px solid #f0f2f5' : 'none',
                background: notif.read ? '#fff' : '#f7f9ff',
                cursor: 'pointer',
                transition: 'background 0.15s',
                position: 'relative',
              }}
              onMouseEnter={e => {
                (e.currentTarget).style.background = notif.read ? '#fafbfc' : '#f0f4fa';
              }}
              onMouseLeave={e => {
                (e.currentTarget).style.background = notif.read ? '#fff' : '#f7f9ff';
              }}
            >
              {/* Unread dot */}
              {!notif.read && (
                <div
                  style={{
                    position: 'absolute',
                    left: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: '#0078d4',
                  }}
                />
              )}

              {/* Category icon */}
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 7,
                  background: cs.bg,
                  border: `1px solid ${cs.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={cs.color} strokeWidth="1.8">
                  <path d={cs.icon} />
                </svg>
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minHeight: 'auto',
                    marginBottom: -16,

                    marginTop: "-15px"
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#161b2e' }}>{notif.title}</span>
                  <span
                    style={{
                      padding: '1px 6px',
                      borderRadius: 3,
                      fontSize: 10,
                      fontWeight: 600,
                      background: cs.bg,
                      color: cs.color,
                      border: `0.5px solid ${cs.border}`,
                      flexShrink: 0,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {notif.category}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: 11.5,
                    color: '#5c6070',
                    margin: 0,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    marginBottom: -6,
                  }}
                >
                  {notif.body}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: -3 }}>
                  {notif.projectId && (
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        color: '#0078d4',
                        background: '#e8f4fd',
                        lineHeight: 1,
                        padding: '3px 5px',
                        
                        borderRadius: 3,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {notif.projectId}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: '#b0b8cc', fontFamily: 'monospace' }}>{timeAgo(notif.time)}</span>
                </div>
              </div>

              {/* Dismiss */}
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDismiss(notif.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#c8ccd6',
                  padding: 2,
                  borderRadius: 4,
                  flexShrink: 0,
                  display: 'flex',
                  alignSelf: 'flex-start',
                  marginTop: 0,
                }}
                onMouseEnter={e => ((e.currentTarget).style.color = '#a4262c')}
                onMouseLeave={e => ((e.currentTarget).style.color = '#c8ccd6')}
                title="Dismiss"
              >
                <Icon d={ICONS.x} size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: '7px 12px', borderTop: '1px solid #f0f2f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: '#8b92a5', lineHeight: 2 }}>
          {notifications?.length} total · {unreadCount} unread
        </span>
        <button
          onClick={() => {
            onClose();
            onViewAll?.();
          }}
          style={{
            fontSize: 12,
            color: '#0078d4',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            padding: 0,
            lineHeight: 1,
          }}
        >
          View all notifications →
        </button>
      </div>
    </div>
  );
}